/*
 * Wspólne dane i funkcje stron docelowych pod reklamy (Landing.jsx) —
 * celowo w .mjs i bez zależności od Reacta, bo ten sam plik czyta skrypt
 * prerenderu (scripts/prerender-landings.mjs) przy budowaniu statycznego
 * HTML-a, a przeglądarka — przy normalnym renderze. Dzięki temu pierwszy
 * ekran z HTML-a (to, co widzi Google i co daje LCP) i pierwszy ekran z Reacta
 * biorą te same teksty i te same reguły z jednego miejsca.
 */

export const STRAPLINE = "Usługi prawne online. Pomoc radców prawnych w każdej sprawie.";

/* Musi odpowiadać CATEGORIES w pages/NewCase.jsx — lista kategorii z formularza zamówienia. */
export const CATEGORIES = [
  "Prawo pracy",
  "Najem i nieruchomości",
  "Prawo konsumenckie",
  "Prawo rodzinne",
  "Spadki",
  "Umowy i zobowiązania",
  "Inne",
];

/* Minimalna długość opisu sprawy: tylko niepusty (musi odpowiadać DESC_MIN w pages/NewCase.jsx i prawly_server/server.js). */
export const DESC_MIN = 1;

export const KIND_LABEL = { opinion: "Opinia prawna", document: "Dokument prawny", bundle: "Pakiet: opinia + dokument" };
export const KINDS = ["opinion", "document", "bundle"];

export const TRUST_CHIPS = ["Podpis i numer wpisu radcy prawnego", "Stała cena znana z góry"];

export const DELIVERABLES = [
  {
    kind: "opinion",
    title: "Opinia prawna",
    lead: "Pisemna ocena Twojej sprawy, do której możesz wracać.",
    points: [
      "Ocena prawna Twojej sytuacji na podstawie opisu i dokumentów",
      "Podstawa prawna — konkretne przepisy",
      "Najważniejsze terminy i ryzyka",
      "Rekomendowane kolejne kroki",
      "PDF z podpisem elektronicznym, imieniem, nazwiskiem i numerem wpisu radcy prawnego",
    ],
  },
  {
    kind: "document",
    title: "Dokument prawny",
    lead: "Gotowy do użycia dokument przygotowany pod Twoją sprawę.",
    points: [
      "Kompletny projekt pisma, wezwania, wypowiedzenia lub umowy",
      "Treść dopasowana do faktów i wytycznych ze zlecenia",
      "Dane do uzupełnienia wyraźnie oznaczone",
      "PDF z podpisem elektronicznym, imieniem, nazwiskiem i numerem wpisu radcy prawnego",
    ],
  },
  {
    kind: "bundle",
    title: "Pakiet: opinia + dokument",
    lead: "Ocena sprawy i gotowe pismo w jednym zamówieniu.",
    points: [
      "Opinia prawna z oceną, podstawą prawną i zaleceniami",
      "Dokument przygotowany spójnie z opinią — te same fakty i ta sama linia argumentacji",
      "Jedna cena i jeden termin dla obu części",
      "Oba pliki w PDF, podpisane przez radcę prawnego",
    ],
  },
];

export const STEPS = [
  {
    t: "Opisujesz sprawę",
    d: "W formularzu własnymi słowami — fakty, daty, czego oczekujesz. Dokumenty dołączasz w następnym kroku.",
  },
  {
    t: "Płacisz online",
    d: "Cenę i termin widzisz przed płatnością. Konto zakładasz przy składaniu zamówienia, bez logowania na początku.",
  },
  {
    t: "Otrzymujesz podpisany PDF",
    d: "Radca prawny przygotowuje opinię lub dokument i podpisuje go elektronicznie, z imieniem, nazwiskiem i numerem wpisu. Gotowy PDF dostajesz {{termin}} od zaksięgowania płatności — czeka w panelu Twojej sprawy.",
  },
];

/* Do pierwszego ekranu, gdy cennik z API jeszcze się nie załadował albo jest niedostępny. */
export const TERM_FALLBACK = "Termin podany przed płatnością";

/* "149 zł" (bez ",00" przy pełnych złotych), "149,50 zł" gdy są grosze. */
export const zl = (cents) => {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** Najtańsza aktywna usługa danego rodzaju (API sortuje po rodzaju i cenie). */
export const pickService = (services, kind) =>
  (Array.isArray(services) ? services : []).find((s) => s.kind === kind && s.price_cents > 0) || null;

/* Termin realizacji jest w GODZINACH ROBOCZYCH — liczbę bierzemy z cennika (API),
   nigdy z tekstu na sztywno, żeby nagłówek, kafelki, kroki i FAQ mówiły to samo.
   termHours = najkrótszy termin spośród oferowanych usług (reguła ogólna, użyta w
   FAQ i opisach); termin konkretnej usługi (np. pakietu) pokazujemy przy niej. */
export const TERM_PHRASE_FALLBACK = "w terminie podanym przed płatnością";

export function termHours(services) {
  const hours = KINDS.map((k) => pickService(services, k)?.sla_hours).filter(Boolean);
  return hours.length ? Math.min(...hours) : null;
}

/** "do 12 godzin roboczych" (1 → "do 1 godziny roboczej"). */
export const termPhrase = (h) => (h ? `do ${h} ${Number(h) === 1 ? "godziny roboczej" : "godzin roboczych"}` : TERM_PHRASE_FALLBACK);

/* Awaryjne ceny, gdy cennik z API jest chwilowo niedostępny (normalnie liczby biorą się z cennika). */
const PRICE_FALLBACK = { opinion: "149 zł", document: "299 zł", bundle: "399 zł" };
const PRICE_PLACEHOLDERS = { "{{cena_opinia}}": "opinion", "{{cena_dokument}}": "document", "{{cena_pakiet}}": "bundle" };

/** Podstawia {{termin}} oraz {{cena_opinia}}, {{cena_dokument}}, {{cena_pakiet}} w tekstach z konfiguracji
    (FAQ, opisy SEO) wartościami z cennika, żeby tekst nigdy nie rozjechał się z faktycznymi cenami i terminem. */
export function fillTerm(text, services) {
  let out = String(text ?? "").split("{{termin}}").join(termPhrase(termHours(services)));
  for (const [ph, kind] of Object.entries(PRICE_PLACEHOLDERS)) {
    if (!out.includes(ph)) continue;
    const svc = (Array.isArray(services) ? services : []).find((x) => x.kind === kind && x.price_cents > 0);
    out = out.split(ph).join(svc ? zl(svc.price_cents) : PRICE_FALLBACK[kind]);
  }
  return out;
}

/* Krótkie nazwy w linii cenowej strony z cennikiem. */
const PRICE_NAME = { opinion: "Opinia", document: "Dokument", bundle: "Pakiet" };

/** Ogólna linia pod nagłówkiem (nie nazywa jednej usługi): "Usługi prawne od 149 zł · odpowiedź do 12 godzin roboczych". */
export function heroPriceLine(services, layout) {
  if (layout === "cennik") {
    const parts = KINDS.map((k) => ({ k, s: pickService(services, k) })).filter((x) => x.s);
    return parts.length ? parts.map(({ k, s }) => `${PRICE_NAME[k]} ${zl(s.price_cents)}`).join(" · ") : "";
  }
  const prices = KINDS.map((k) => pickService(services, k)?.price_cents).filter(Boolean);
  if (!prices.length) return "";
  return `Usługi prawne od ${zl(Math.min(...prices))} · odpowiedź ${termPhrase(termHours(services))}`;
}

/** Temat sprawy z początku opisu — formularz zamówienia wymaga tytułu, a odwiedzający z reklamy go nie wpisuje. */
export function makeTitle(description) {
  const flat = String(description || "").replace(/\s+/g, " ").trim();
  if (flat.length <= 70) return flat;
  const cut = flat.slice(0, 70);
  const at = cut.lastIndexOf(" ");
  return `${(at > 40 ? cut.slice(0, at) : cut).replace(/[,;:\-–]+$/, "")}…`;
}

export const slugPath = (slug) => `/${String(slug || "").replace(/^\/+|\/+$/g, "")}`;

/* Lewa kolumna pierwszego ekranu na szerokim ekranie: lista korzyści (czysty
   HTML, bez skryptów — nie wpływa na LCP). Wspólna dla Reacta (Landing.jsx) i
   prerenderu, żeby statyczny pierwszy ekran i ten z Reacta były identyczne.
   Na telefonie ukryta (CSS) — tam formularz ma być jak najbliżej góry. Całość
   pierwszego ekranu ma się mieścić w wysokości okna, więc bez dodatkowych
   ilustracji: lewa kolumna ma mniej więcej wysokość karty formularza. */
export const HERO_TICKS = [
  "Podpisany PDF — z elektronicznym podpisem radcy prawnego",
  "Imię, nazwisko i numer wpisu prawnika, który odpowiada za treść",
  "Stała cena i termin znane przed płatnością",
  "Możliwość zadania pytań uzupełniających radcy prawnemu po otrzymaniu odpowiedzi",
];

export function heroMoreHtml() {
  // Strzałka "przewiń niżej" (kotwica #dalej = pierwsza sekcja pod hero, patrz Landing.jsx) —
  // pozycjonowana względem całego hero, a w tym bloku siedzi, żeby znikać razem z nim na telefonie.
  return (
    `<div class="lp-hero-more"><ul class="lp-ticks lp-hero-ticks">${HERO_TICKS.map((t) => `<li>${t}</li>`).join("")}</ul>` +
    `<a href="#dalej" class="lp-scroll" aria-label="Przewiń do szczegółów oferty"><span></span></a></div>`
  );
}

/* Pod listą tematów na każdej stronie docelowej — tematy to przykłady, nie ograniczenie oferty. */
export const TOPICS_NOTE = "To tylko przykłady — pomagamy w każdej sprawie prawnej. Opisz swoją, a radca prawny oceni, jak może Ci pomóc.";


/* ---------------------------------------------------------------------------
   Sekcje pod pierwszym ekranem jako HTML — jedno źródło dla prerenderu
   (treść w HTML-u bez JavaScriptu, dla robotów) i dla Reacta (dangerouslySetInnerHTML),
   więc obie wersje nie mogą się rozjechać. Teksty z CMS-a zawsze przez esc().
   --------------------------------------------------------------------------- */

/** Podgląd 1–2 stron zanonimizowanej opinii + otwarcie PDF (wiersz pod kafelkami w "Co otrzymujesz"). Pusty bez plików. */
function sampleBlock(shared) {
  const sm = shared?.sample || {};
  const pages = (Array.isArray(sm.pages) ? sm.pages : []).map((x) => String(x || "").trim()).filter(Boolean).slice(0, 2);
  const pdf = String(sm.pdf || "").trim();
  if (!pages.length && !pdf) return "";
  const open = pdf || pages[0];
  const thumbs = pages
    .map(
      (src, i) =>
        `<a class="lp-sample-page" href="${esc(pdf || src)}" target="_blank" rel="noopener" aria-label="Otwórz przykładową opinię — strona ${i + 1}">` +
        `<img src="${esc(src)}" alt="Przykładowa opinia prawna, strona ${i + 1} (dane zanonimizowane)" loading="lazy" decoding="async" /></a>`
    )
    .join("");
  return (
    `<div class="lp-sample${pages.length ? "" : " lp-sample-solo"}">${pages.length ? `<div class="lp-sample-pages">${thumbs}</div>` : ""}` +
    `<div class="lp-sample-t"><h3>${esc(sm.title || "Zobacz, co otrzymasz")}</h3>${sm.lead ? `<p>${esc(sm.lead)}</p>` : ""}` +
    `<a class="btn ghost" href="${esc(open)}" target="_blank" rel="noopener">Otwórz przykład${pdf ? " (PDF)" : ""}</a></div></div>`
  );
}

/** "Co dokładnie otrzymujesz": zawsze opinia i dokument obok siebie (wariant pierwszy), pakiet jako dopisek; przykład pod spodem. */
export function receiveHtml(services, kind, shared, anchor = true) {
  const order = kind === "document" ? ["document", "opinion"] : ["opinion", "document"];
  const boxes = order
    .map((k) => {
      const d = DELIVERABLES.find((x) => x.kind === k);
      const sv = pickService(services, k);
      return (
        `<article class="lp-box"><h3>${esc(d.title)}${sv ? `<span class="lp-box-price">${esc(zl(sv.price_cents))}</span>` : ""}</h3>` +
        `<p class="lp-box-lead">${esc(d.lead)}</p><ul class="lp-ticks">${d.points.map((x) => `<li>${esc(x)}</li>`).join("")}</ul></article>`
      );
    })
    .join("");
  const bundle = pickService(services, "bundle");
  return (
    `<section class="lp-sec"${anchor ? ' id="dalej"' : ""}><div class="lp-wrap"><h2>Co dokładnie otrzymujesz</h2>` +
    `<div class="lp-receive">${boxes}${sampleBlock(shared)}</div>` +
    `<p class="lp-others">${bundle ? `Pakiet: opinia + dokument (${esc(zl(bundle.price_cents))}) — ` : "Pakiet: opinia + dokument — "}wybierzesz w formularzu.</p></div></section>`
  );
}

export function stepsHtml(services, note = "") {
  return (
    `<section class="lp-sec lp-alt"><div class="lp-wrap"><h2>Jak to działa</h2>` +
    (String(note || "").trim() ? `<p class="lp-how-note">${esc(note)}</p>` : "") +
    `<ol class="lp-steps">` +
    STEPS.map(
      (st, i) => `<li><span class="lp-step-n">${i + 1}</span><h3>${esc(st.t)}</h3><p>${esc(fillTerm(st.d, services))}</p></li>`
    ).join("") +
    `</ol></div></section>`
  );
}

/** Braki w treściach wspólnych i opiniach — do listy w panelu admina (puste = element się nie wyświetla). */
export function sharedGaps(shared, reviews) {
  const sh = shared || {};
  const sm = sh.sample || {};
  const rv = reviews || {};
  const has = (v) => String(v ?? "").trim().length > 0;
  return [
    { ok: has(sm.pdf) || (sm.pages || []).some(has), label: "Przykładowa opinia: plik PDF i/lub obrazy stron" },
    { ok: has(sh.risk), label: "Zdanie o ryzyku płatności z góry (przy przycisku zamówienia)" },
    { ok: has(sh.confidentiality), label: "Informacja o poufności (przy polu opisu sprawy)" },
    { ok: (rv.items || []).some((x) => has(x?.text)), label: "Opinie Google OLC: treść opinii (/admin/opinie-google)" },
    { ok: has(rv.url), label: "Opinie Google OLC: link do wizytówki" },
  ];
}

/* Który temat opinii pasuje do strony docelowej (po adresie) — patrz reviewsHtml({ topic }). */
const REVIEW_TOPICS = [
  [/prawo-pracy|praca|pracy/, /(?:^|[^\p{L}])(pracodawc|pracownik|wypowiedzeni|mobbing|wynagrodzeni|zatrudnieni)/iu],
  [/umow/, /(?:^|[^\p{L}])(umow|regulamin|wykonawc|zaliczk)/iu],
  [/pism|dokument/, /(?:^|[^\p{L}])(pism|wezwan|dokument|regulamin|wykonawc|zaliczk)/iu],
];
export const reviewTopic = (slug) =>
  (REVIEW_TOPICS.find(([re]) => re.test(String(slug || ""))) || [null, /(?:^|[^\p{L}])(informacj|doradztw|opini|porad)/iu])[1];

/* Opinie, których nie pokazujemy na landingach „radca prawny”: ta strona mówi o radcach prawnych, więc opinia o „Pani
   Adwokat” kłóciłaby się z przekazem. Nie edytujemy opinii, tylko nie wybieramy jej do pierwszych trzech. */
export const REVIEW_AVOID = /(?:^|[^\p{L}])adwokat/iu;

/** Dostawca usługi (schema.org Organization) z danych w stopce: nazwa operatora, adres, telefon, e-mail. */
export function providerLd(settings, siteUrl) {
  const [street, rest] = String(settings?.address || "").split(",").map((x) => x.trim());
  const m = rest?.match(/^(\d{2}-\d{3})\s+(.+)$/);
  const address = m
    ? { streetAddress: street, postalCode: m[1], addressLocality: m[2] }
    : settings?.address
      ? { streetAddress: settings.address }
      : null;
  const org = {
    "@type": "Organization",
    name: settings?.company || settings?.brand || "prawly",
    url: siteUrl,
  };
  if (settings?.email) org.email = settings.email;
  if (settings?.phone) org.telephone = settings.phone;
  if (address) org.address = { "@type": "PostalAddress", addressCountry: "PL", ...address };
  return org;
}


/* ---------------------------------------------------------------------------
   Strona z cennikiem (landing z layout: "cennik", adres /cennik/): karty trzech usług z ceną, terminem i zakresem
   zamiast bloku „Co dokładnie otrzymujesz”. Ceny i terminy z cennika (API), nie z tekstu na sztywno.
   Linki do formularza to zwykłe <a href="/panel/nowa?kind=…"> (działają bez JavaScriptu); po załadowaniu
   aplikacji Landing.jsx przechwytuje kliknięcie i przechodzi do zamówienia bez przeładowania strony.
   --------------------------------------------------------------------------- */
export function priceCardsHtml(services) {
  const cards = KINDS.map((k) => ({ k, sv: pickService(services, k), d: DELIVERABLES.find((x) => x.kind === k) }))
    .filter((x) => x.sv && x.d)
    .map(
      ({ k, sv, d }) =>
        `<article class="lp-pc"><h3>${esc(d.title)}</h3>` +
        `<p class="lp-pc-price">${esc(zl(sv.price_cents))}<span> brutto</span></p>` +
        `<p class="lp-pc-meta">Termin: ${esc(termPhrase(sv.sla_hours))}<br />Przygotowuje: radca prawny, podpis z numerem wpisu</p>` +
        `<p class="lp-box-lead">${esc(d.lead)}</p><ul class="lp-ticks">${d.points.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` +
        `<a class="btn wax" href="/panel/nowa?kind=${k}" data-kind="${k}">Zamów — ${esc(zl(sv.price_cents))}</a></article>`
    )
    .join("");
  if (!cards) return "";
  return (
    `<section class="lp-sec" id="dalej"><div class="lp-wrap"><h2>Cennik usług radcy prawnego</h2>` +
    `<p class="lp-sub">Opinia prawna — cena i zakres: każda usługa ma stałą cenę i termin, które widzisz przed płatnością.</p>` +
    `<div class="lp-prices">${cards}</div></div></section>`
  );
}

/** „Co jest w cenie / czego nie ma” — listy z konfiguracji strony (pola included, notIncluded). */
export function includedHtml(l) {
  const inc = (l?.included || []).filter(Boolean);
  const exc = (l?.notIncluded || []).filter(Boolean);
  if (!inc.length && !exc.length) return "";
  const list = (items, cls) => `<ul class="lp-ticks${cls}">${items.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>`;
  return (
    `<section class="lp-sec lp-alt"><div class="lp-wrap"><h2>Co jest w cenie, a czego w niej nie ma</h2><div class="lp-incl">` +
    (inc.length ? `<div><h3>${esc(l.includedTitle || "W cenie")}</h3>${list(inc, "")}</div>` : "") +
    (exc.length ? `<div><h3>${esc(l.notIncludedTitle || "Poza ceną")}</h3>${list(exc, " lp-cross")}</div>` : "") +
    `</div></div></section>`
  );
}
